import React, { useState } from 'react';
import UserSearchAutocomplete from './UserSearchAutocomplete';
import { shareAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const ShareDialog = ({ isOpen, onClose, onSuccess, defaultResourceType = null, defaultResourceId = null }) => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [resourceType, setResourceType] = useState(defaultResourceType || 'all');
  const [shareAllResources, setShareAllResources] = useState(defaultResourceId === null);
  const [permission, setPermission] = useState('view');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setError(t('sharing.selectUser'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const shareData = {
        sharedWithId: selectedUser.id,
        resourceType,
        resourceId: shareAllResources ? null : defaultResourceId,
        permission,
        expiresAt: expiresAt || null
      };

      await shareAPI.createShare(shareData);

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('sharing.failedToCreate'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setResourceType(defaultResourceType || 'all');
    setShareAllResources(defaultResourceId === null);
    setPermission('view');
    setExpiresAt('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500">
          <h2 className="text-2xl font-bold text-white">{t('sharing.shareResources')}</h2>
          <button
            className="text-white hover:text-gray-200 transition-colors text-3xl font-light leading-none"
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sharing.shareWithUser')}*</label>
              <UserSearchAutocomplete
                onSelect={handleUserSelect}
                selectedUser={selectedUser}
              />
              {selectedUser && (
                <div className="mt-2 px-3 py-2 bg-pastel-purple-100 dark:bg-pastel-purple-900/30 text-pastel-purple-800 dark:text-pastel-purple-200 rounded-lg text-sm">
                  {t('sharing.selected')}: <strong>{selectedUser.username}</strong> ({selectedUser.email})
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sharing.resourceType')}*</label>
              <select
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
                disabled={defaultResourceType !== null}
              >
                <option value="all">{t('sharing.allResources')}</option>
                <option value="invoice">{t('sharing.invoices')}</option>
                <option value="cost">{t('sharing.costs')}</option>
                <option value="template">{t('sharing.templates')}</option>
              </select>
            </div>

            {defaultResourceId && (
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareAllResources}
                    onChange={(e) => setShareAllResources(e.target.checked)}
                    className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('sharing.shareAllInstead', { type: resourceType })}
                  </span>
                </label>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sharing.permission')}*</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="permission"
                    value="view"
                    checked={permission === 'view'}
                    onChange={(e) => setPermission(e.target.value)}
                    className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 focus:ring-pastel-purple-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('sharing.viewOnlyOption')}</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="permission"
                    value="edit"
                    checked={permission === 'edit'}
                    onChange={(e) => setPermission(e.target.value)}
                    className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 focus:ring-pastel-purple-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('sharing.viewAndEditOption')}</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sharing.expirationDate')}</label>
              <input
                type="date"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <small className="text-xs text-gray-500 dark:text-gray-400">{t('sharing.expirationHelp')}</small>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <button
              type="button"
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={loading}
            >
              {t('sharing.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 hover:from-pastel-purple-700 hover:to-pastel-pink-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !selectedUser}
            >
              {loading ? t('sharing.creating') : t('sharing.share')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShareDialog;
